import { useStores } from "../../stores/root-store";
import { observer } from "mobx-react";
import _, { isEmpty, toNumber } from "lodash";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  currentDateString,
  formatDate,
  getEnumOptions,
  ShowAlert,
} from "../../utils/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  ApiResponse,
  IModal,
  OfferTypeEnum,
  SelectOption,
} from "../../stores/index.d";
import DatePicker from "react-datepicker";
import "../../date-picker-custom.css";
import Select, { ActionMeta, SingleValue, StylesConfig } from "react-select";
import { IProductOffer } from "../../stores/productOffer";
import moment from "moment";

const ProductOfferForm = observer(({ show, setShowModal }: IModal) => {
  const { productOfferStore, productStore } = useStores();
  const { products, fetchProducts } = productStore;
  const { saveOffer, editOffer, fetchOffers } = productOfferStore;

  const isEditRequest = editOffer != null && editOffer.productOfferId > 0;

  const [offer, setOffer] = useState<IProductOffer>(
    isEditRequest
      ? editOffer
      : {
          productOfferId: 0,
          productId: 0,
          offerType: 0,
          discountPercentage: 0,
          freeAdditionalProductId: 0,
          multiBuyDiscountPercentage: 0,
          multiBuyQuantity: 0,
          startDate: currentDateString(),
          endDate: currentDateString(),
          product: null,
          freeProduct: null,
        }
  );

  const [isFormValid, setIsFormValid] = useState(false);

  const offerTypeList = getEnumOptions(OfferTypeEnum) as SelectOption[];

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const valid =
      offer.productId > 0 &&
      offer.offerType > 0 &&
      !isEmpty(offer.startDate) &&
      offer.startDate != null &&
      !isEmpty(offer.endDate) &&
      offer.endDate != null &&
      (offer.offerType == OfferTypeEnum.Buy1Get1 ||
        (offer.offerType == OfferTypeEnum.Discount &&
          offer.discountPercentage > 0) ||
        (offer.offerType == OfferTypeEnum.FreeProduct &&
          offer.freeAdditionalProductId > 0) ||
        (offer.offerType == OfferTypeEnum.MultiBuys &&
          offer.multiBuyQuantity > 0 &&
          offer.multiBuyDiscountPercentage > 0));
    setIsFormValid(valid);
  }, [offer]);

  const productOpts = useMemo(() => {
    return products?.map((a) => {
      return {
        label: a.productName,
        value: a.productId.toString(),
      } as SelectOption;
    });
  }, [products]);

  const mapProductOption = (id: number) => {
    return productOpts.find((p) => p.value == id.toString());
  };

  const mapOfferTypeOption = (id: number) => {
    var dd = offerTypeList.find((p) => p.value == id.toString());
    return dd;
  };

  const handleSelectChange = (
    evt: SingleValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    const { name } = actionMeta;
    if (name) setOffer((prev) => ({ ...prev, [name]: evt?.value }));

    if (name == "offerType") {
      setOffer((prev) => ({
        ...prev,
        ["discountPercentage"]: 0,
        ["freeAdditionalProductId"]: 0,
        ["multiBuyDiscountPercentage"]: 0,
        ["multiBuyQuantity"]: 0,
      }));
    }

    if (name == "productId") {
      setOffer((prev) => ({
        ...prev,
        ["freeAdditionalProductId"]: 0,
      }));
    }
  };

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = evt.target;
    setOffer((prev) => ({ ...prev, [id]: value }));
  };

  const handleDateChange = (dt: Date | null, name: string) => {
    setOffer((prev) => ({
      ...prev,
      [name]: dt ? formatDate(dt, "YYYY-MM-DD") : "",
    }));
  };

  const submitCallback = useCallback((res: ApiResponse<number>) => {
    if (res && res.isSuccess) {
      setShowModal(false);
      fetchOffers();
      ShowAlert.Success(
        `Offer ${editOffer?.id ? "updated" : "added"} successfully.`
      );
    } else {
      ShowAlert.Error(res?.message ?? "Request failed. Please try again.");
    }
  }, []);

  const handleFormSubmit = () => {
    if (!isFormValid) {
      ShowAlert.Error("One or more field value missing.");
      return;
    }

    saveOffer(offer, submitCallback);
  };

  return (
    <Modal show={show} onHide={() => setShowModal(false)} className="modal-lg">
      <ModalHeader closeButton>
        <Modal.Title>
          <h5>{isEditRequest ? "Update" : "Add"} Offer</h5>
        </Modal.Title>
      </ModalHeader>
      <ModalBody>
        <form>
          <div className="row">
            <div className="col mb-3">
              <label>Product</label>
              <Select
                defaultValue={mapProductOption(offer.productId)}
                name="productId"
                options={productOpts}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Choose product"
                onChange={handleSelectChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col mb-3">
              <label>Offer type</label>
              <Select
                defaultValue={mapOfferTypeOption(offer.offerType)}
                name="offerType"
                options={offerTypeList}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Choose offer type"
                onChange={handleSelectChange}
              />
            </div>
          </div>

          {offer.offerType == OfferTypeEnum.FreeProduct && (
            <div className="row">
              <div className="col mb-3">
                <label>Free product</label>
                <Select
                  defaultValue={mapProductOption(offer.freeAdditionalProductId)}
                  name="freeAdditionalProductId"
                  options={productOpts.filter(
                    (p) => p.value != offer.productId.toString()
                  )}
                  className="basic-select"
                  classNamePrefix="select"
                  placeholder="Choose free product"
                  onChange={handleSelectChange}
                />
              </div>
            </div>
          )}

          {offer.offerType == OfferTypeEnum.Discount && (
            <div className="form-floating mb-3">
              <input
                type="number"
                className="form-control"
                id="discountPercentage"
                placeholder="Discount %"
                value={offer.discountPercentage}
                onChange={handleInputChange}
              />
              <label htmlFor="discountPercentage" style={{ zIndex: 0 }}>
                Discount %
              </label>
            </div>
          )}

          {offer.offerType == OfferTypeEnum.MultiBuys && (
            <div className="row">
              <div className="col">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="multiBuyQuantity"
                    placeholder="Multi Buy Quantity"
                    value={offer.multiBuyQuantity}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="multiBuyQuantity" style={{ zIndex: 0 }}>
                    Multi Buy Quantity
                  </label>
                </div>
              </div>
              <div className="col">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="multiBuyDiscountPercentage"
                    placeholder="Multi Buy Discount %"
                    value={offer.multiBuyDiscountPercentage}
                    onChange={handleInputChange}
                  />
                  <label
                    htmlFor="multiBuyDiscountPercentage"
                    style={{ zIndex: 0 }}
                  >
                    Multi Buy Discount %
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <label>Offer duration</label>
            <div className="col">
              <div className="form-floating mb-3">
                <DatePicker
                  selected={offer.startDate as unknown as Date}
                  onChange={(date) => handleDateChange(date, "startDate")}
                  minDate={moment().toDate()}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Start Date"
                  className="form-control"
                  wrapperClassName="d-block"
                />
              </div>
            </div>
            <div className="col">
              <div className="form-floating mb-3">
                <DatePicker
                  selected={offer.endDate as unknown as Date}
                  onChange={(date) => handleDateChange(date, "endDate")}
                  minDate={moment().toDate()}
                  dateFormat="dd MMM yyyy"
                  placeholderText="End Date"
                  className="form-control"
                  wrapperClassName="d-block"
                />
              </div>
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-default flex-fill"
          onClick={handleFormSubmit}
          disabled={!isFormValid}
        >
          {isEditRequest ? (
            <>
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              <span>Update Offer</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              <span>Add Offer</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
});

export default ProductOfferForm;
